import { mentionHelpers, openSplitViewState } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import PlaceholderSuggestionList from 'modules/components/PlaceholderSuggestionList/PlaceholderSuggestionList';
import { domain } from 'modules/data';
import { useUserProfile } from 'modules/utils';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { emailToSupplierHelpers } from 'shared/helpers';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { Form } from './components/Form/Form';
import { FormOld } from './components/FormOld/FormOld';
import { getContactsPersonalData } from './EmailToSupplierForm.helpers';
import { messages } from './EmailToSupplierForm.messages';
import { EmailToSupplierFormProps, GetMessageFn, MentionConfig } from './EmailToSupplierForm.types';

const EmailToSupplierForm: FC<EmailToSupplierFormProps> = (props) => {
    const { data, contacts, labels, placeholderList } = props;

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const setContentSplitView = useSetAtom(contentSplitViewState);

    const [localBody, setLocalBody] = useState(data?.body || '');
    const [localSubject, setLocalSubject] = useState(data?.subject || '');

    const profile = useUserProfile();

    useEffect(() => {
        if (data?.body) {
            const newBody = emailToSupplierHelpers.addMentionExtensionTags(data.body, placeholderList || []);

            setLocalBody(newBody);
        }
    }, [data?.body, placeholderList]);

    useEffect(() => {
        if (data?.subject) {
            const newSubject = emailToSupplierHelpers.addMentionExtensionTags(data.subject, placeholderList || []);

            setLocalSubject(newSubject);
        }
    }, [data?.subject, placeholderList]);

    const emailOptions = useMemo(() => getContactsPersonalData(contacts), [contacts]);

    const getMessage = useCallback<GetMessageFn>(
        (key) => {
            return labels && key in labels ? labels[key] : messages[key];
        },
        [labels]
    );

    const customMentionConfig = useMemo<MentionConfig>(
        () => ({
            renderLabel({ node }) {
                const name = node.attrs.placeholderName;

                return `[${name}]`;
            },
            suggestion: {
                ...mentionHelpers.createSuggestion({ mentionListComponent: PlaceholderSuggestionList }),
                char: '[',
                items: ({ query }: { query: string }) =>
                    (placeholderList || [])
                        .filter((item) => item.toLowerCase().includes(query.toLowerCase()))
                        .map((item) => ({
                            placeholderName: item,
                        })),
            },
        }),
        [placeholderList]
    );

    const handleClickOnAttachment = useCallback(
        (attachmentId: string) => {
            setOpenSplitView(true);
            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToEmailToSupplier,
                attachmentId,
            });
        },
        [setContentSplitView, setOpenSplitView]
    );

    const showFormRedesign = profile.betaFeatures.includes(domain.ProfileBetaFeature.BelgradeUIEmailToSupplier);

    return showFormRedesign ? (
        <Form
            {...props}
            body={localBody}
            subject={localSubject}
            getMessage={getMessage}
            emailOptions={emailOptions}
            customMentionConfig={customMentionConfig}
            handleClickOnAttachment={handleClickOnAttachment}
        />
    ) : (
        <FormOld
            {...props}
            body={localBody}
            subject={localSubject}
            getMessage={getMessage}
            emailOptions={emailOptions}
            customMentionConfig={customMentionConfig}
            handleClickOnAttachment={handleClickOnAttachment}
        />
    );
};

EmailToSupplierForm.displayName = 'EmailToSupplierForm';

export default memo(EmailToSupplierForm);
